import { createSelector } from 'reselect';

import {
  PlanProgressGraphFieldItem,
  PlanProgressGraphParams,
  PlanProgressTransitionGraph,
} from '../../domain/dataPlanProgressGraph';
import { buildDataPlanProgressGraphListForFields } from '../../domain/dataPlanProgressGraphFields';

import { RootState } from '../../store';

const FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH =
  'FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH' as const;
const FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_REQUEST =
  'FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_REQUEST' as const;
const FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_SUCCESS =
  'FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_SUCCESS' as const;
const RENEW_DATA_PLAN_PROGRESS_TRANSITION_GRAPH =
  'RENEW_DATA_PLAN_PROGRESS_TRANSITION_GRAPH' as const;
const HIDE_ALL_DATA_PLAN_PROGRESS_TRANSITION_GRAPH =
  'HIDE_ALL_DATA_PLAN_PROGRESS_TRANSITION_GRAPH' as const;

const SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH =
  'SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH' as const;
const SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_HALL =
  'SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_HALL' as const;
const SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_DATE =
  'SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_DATE' as const;
const SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_FIELDS =
  'SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_FIELDS' as const;
const SET_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_FIELDS =
  'SET_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_FIELDS' as const;
const SET_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_GRAPH_LIST =
  'SET_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_GRAPH_LIST' as const;

export const DataPlanProgressTransitionGraphActionTypes = {
  FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH,
  FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_REQUEST,
  FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_SUCCESS,
  RENEW_DATA_PLAN_PROGRESS_TRANSITION_GRAPH,
  HIDE_ALL_DATA_PLAN_PROGRESS_TRANSITION_GRAPH,
  SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH,
  SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_FIELDS,
  SET_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_FIELDS,
  SET_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_GRAPH_LIST,
};

/**
 * 推移グラフデータを取得する
 */
function fetchDataPlanProgressGraphAction(params: PlanProgressGraphParams) {
  return {
    type: FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH,
    payload: { params },
  };
}

function fetchDataPlanProgressTransitionGraphRequestAction() {
  return {
    type: FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_REQUEST,
  };
}

function fetchDataPlanProgressTransitionGraphSuccessAction(
  dataPlanProgressTransitionGraph: PlanProgressTransitionGraph
) {
  return {
    type: FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_SUCCESS,
    payload: { dataPlanProgressTransitionGraph },
  };
}

function renewDataPlanProgressTransitionGraphAction() {
  return {
    type: RENEW_DATA_PLAN_PROGRESS_TRANSITION_GRAPH,
  };
}

function searchDataPlanProgressTransitionGraphAction(
  shuGroupId: string[],
  shuList: string[],
  halls: string[],
  field?: string
) {
  return {
    type: SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH,
    payload: { shuGroupId, shuList, halls, field },
  };
}

function searchDataPlanProgressTransitionGraphHallAction(hall: string[]) {
  return {
    type: SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_HALL,
    payload: { hall },
  };
}

function searchDataPlanProgressTransitionGraphDateAction(
  month: string,
  year: string
) {
  return {
    type: SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_DATE,
    payload: { month, year },
  };
}

/**
 * 指定したグラフ項目で推移グラフデータを再取得（単一項目・後方互換）
 */
function searchDataPlanProgressTransitionGraphFieldsAction(
  field: string | undefined
) {
  return {
    type: SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_FIELDS,
    payload: { field },
  };
}

/**
 * 複数グラフ項目を再取得する
 */
function setDataPlanProgressTransitionGraphFieldsAction(fields: string[]) {
  return {
    type: SET_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_FIELDS,
    payload: { fields },
  };
}

/**
 * 削除のみの場合に graphList をローカル更新する
 */
function setDataPlanProgressTransitionGraphGraphListAction(fields: string[]) {
  return {
    type: SET_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_GRAPH_LIST,
    payload: { fields },
  };
}

function hideAllDataPlanProgressTransitionGraphAction() {
  return {
    type: HIDE_ALL_DATA_PLAN_PROGRESS_TRANSITION_GRAPH,
  };
}

export const DataPlanProgressTransitionGraphActionCreators = {
  fetchDataPlanProgressGraphAction,
  fetchDataPlanProgressTransitionGraphRequestAction,
  fetchDataPlanProgressTransitionGraphSuccessAction,
  renewDataPlanProgressTransitionGraphAction,
  hideAllDataPlanProgressTransitionGraphAction,
  searchDataPlanProgressTransitionGraphAction,
  searchDataPlanProgressTransitionGraphHallAction,
  searchDataPlanProgressTransitionGraphDateAction,
  searchDataPlanProgressTransitionGraphFieldsAction,
  setDataPlanProgressTransitionGraphFieldsAction,
  setDataPlanProgressTransitionGraphGraphListAction,
};

export type FetchDataPlanProgressGraphAction = ReturnType<
  typeof fetchDataPlanProgressGraphAction
>;

type SearchDataPlanProgressTransitionGraphAction = ReturnType<
  typeof searchDataPlanProgressTransitionGraphAction
>;
type SearchDataPlanProgressTransitionGraphHallAction = ReturnType<
  typeof searchDataPlanProgressTransitionGraphHallAction
>;
type SearchDataPlanProgressTransitionGraphDateAction = ReturnType<
  typeof searchDataPlanProgressTransitionGraphDateAction
>;
export type SearchDataPlanProgressTransitionGraphFieldsAction = ReturnType<
  typeof searchDataPlanProgressTransitionGraphFieldsAction
>;
export type SetDataPlanProgressTransitionGraphFieldsAction = ReturnType<
  typeof setDataPlanProgressTransitionGraphFieldsAction
>;
export type SetDataPlanProgressTransitionGraphGraphListAction = ReturnType<
  typeof setDataPlanProgressTransitionGraphGraphListAction
>;

type DataPlanProgressTransitionGraphAction =
  | FetchDataPlanProgressGraphAction
  | SearchDataPlanProgressTransitionGraphAction
  | SearchDataPlanProgressTransitionGraphHallAction
  | SearchDataPlanProgressTransitionGraphDateAction
  | SearchDataPlanProgressTransitionGraphFieldsAction
  | SetDataPlanProgressTransitionGraphFieldsAction
  | SetDataPlanProgressTransitionGraphGraphListAction
  | ReturnType<typeof fetchDataPlanProgressTransitionGraphRequestAction>
  | ReturnType<typeof fetchDataPlanProgressTransitionGraphSuccessAction>
  | ReturnType<typeof renewDataPlanProgressTransitionGraphAction>
  | ReturnType<typeof hideAllDataPlanProgressTransitionGraphAction>;

type DataPlanProgressTransitionGraphState = {
  dataPlanProgressTransitionGraph: PlanProgressTransitionGraph | undefined;
  isLoading: boolean | undefined;
};

const initialState: DataPlanProgressTransitionGraphState = {
  dataPlanProgressTransitionGraph: undefined,
  isLoading: undefined,
};

export const dataPlanProgressTransitionGraphAllSelector = (state: RootState) =>
  state.dataPlanProgressTransitionGraph.dataPlanProgressTransitionGraph;

/**
 * 推移グラフの検索条件を取得する（fields / field を graphList から再構築）
 */
export const dataPlanProgressTransitionGraphSearchConditionSelector = () =>
  createSelector(
    dataPlanProgressTransitionGraphAllSelector,
    (data): PlanProgressGraphParams => {
      if (!data) {
        return {} as PlanProgressGraphParams;
      }
      const fields = data.graphList.map((item) => item.field);
      return {
        ...data.setting,
        field: fields.at(0),
        ...(fields.length > 0 ? { fields } : {}),
      };
    }
  );

export const dataPlanProgressTransitionGraphGraphListSelector = createSelector(
  dataPlanProgressTransitionGraphAllSelector,
  (data): PlanProgressGraphFieldItem[] => data?.graphList ?? []
);

export const dataPlanProgressTransitionGraphLoadingAllSelector = (
  state: RootState
) => state.dataPlanProgressTransitionGraph.isLoading;

export function dataPlanProgressTransitionGraphReducer(
  state = initialState,
  action: DataPlanProgressTransitionGraphAction
): DataPlanProgressTransitionGraphState {
  switch (action.type) {
    case FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_REQUEST:
      return {
        ...state,
        isLoading: true,
      };
    case FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        dataPlanProgressTransitionGraph:
          action.payload.dataPlanProgressTransitionGraph,
      };
    case RENEW_DATA_PLAN_PROGRESS_TRANSITION_GRAPH:
      return initialState;
    case HIDE_ALL_DATA_PLAN_PROGRESS_TRANSITION_GRAPH:
      return {
        ...state,
        isLoading: initialState.isLoading,
        dataPlanProgressTransitionGraph:
          initialState.dataPlanProgressTransitionGraph,
      };
    case SET_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_GRAPH_LIST: {
      const graph = state.dataPlanProgressTransitionGraph;
      if (!graph) {
        return state;
      }
      return {
        ...state,
        dataPlanProgressTransitionGraph: {
          ...graph,
          graphList: buildDataPlanProgressGraphListForFields(
            graph.graphList,
            action.payload.fields
          ),
        },
      };
    }
    default:
      return state;
  }
}
